import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Edit2, Trash2, Plus, Clock } from 'lucide-react';
import { Typography } from '../../../interfaces/typography';
import { CommentDataResult } from "interfaces/sessions";
import { useCommentFunctions } from '../../../lib/redux/sessions/data/comment-hook';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

interface CommentSectionProps {
  sessionId: string;
  transcriptIdx: number;
  comments: CommentDataResult[];
  position: 'left' | 'right';
  onClose: () => void;
  showCommentInput?: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  sessionId,
  transcriptIdx,
  comments,
  position,
  onClose,
  showCommentInput: initialShowCommentInput = false,
}) => {
  const { addComment, addAndSaveComment, updateCommentText, removeComment } = useCommentFunctions();
  const [showCommentInput, setShowCommentInput] = useState(initialShowCommentInput);
  const [showCommentThread, setShowCommentThread] = useState(!initialShowCommentInput);
  const [commentText, setCommentText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Get current user ID
  const currentUser = useSelector((state: RootState) => state.user.value);
  
  // Get session data from Redux store
  const sessionData = useSelector((state: RootState) => 
    state.sessionDetails.sessionIdToDetails[sessionId]?.data
  );

  // Focus the comment input when it appears
  useEffect(() => {
    if ((showCommentInput || isEditing) && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [showCommentInput, isEditing]);

  const handleCloseCommentInput = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCommentInput(false);
    setIsEditing(false);
    setEditingCommentId(null);
    setCommentText('');
  };

  const handleCloseCommentThread = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleAddToThread = () => {
    setShowCommentInput(true);
  };

  const handleSaveComment = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (commentText.trim() === '') {
      setShowCommentInput(false);
      setIsEditing(false);
      return;
    }

    if (isEditing && editingCommentId) {
      // Update existing comment
      updateCommentText(sessionId, editingCommentId, commentText);
      setIsEditing(false);
      setEditingCommentId(null);
    } else {
      // Use addAndSaveComment directly to add and save in one step
      await addAndSaveComment(sessionId, transcriptIdx, commentText);
    }
    
    setCommentText('');
    setShowCommentInput(false);
    // Show the updated comment thread
    setTimeout(() => {
      setShowCommentThread(true);
    }, 200);
  };

  const handleEditComment = (comment: CommentDataResult) => {
    setIsEditing(true);
    setEditingCommentId(comment.comment_id);
    setCommentText(comment.comment);
    setShowCommentThread(false);
    setShowCommentInput(true);
  };

  const handleDeleteComment = (e: React.MouseEvent, commentId: string) => {
    e.stopPropagation();
    removeComment(sessionId, commentId);
    
    if (comments.length <= 1) {
      onClose();
    }
    
    if (editingCommentId === commentId) {
      setIsEditing(false);
      setEditingCommentId(null);
      setCommentText('');
      setShowCommentInput(false);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      const saveButton = document.querySelector('[data-save-comment-button]');
      if (saveButton) {
        (saveButton as HTMLButtonElement).click();
      }
    }
    
    // Close on Escape
    if (e.key === 'Escape') {
      setShowCommentInput(false);
      setIsEditing(false);
      setEditingCommentId(null);
      setCommentText('');
    }
  };

  const formatTimestamp = (date: Date): string => {
    return new Date(date).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const canEditComment = (comment: CommentDataResult): boolean => {
    return comment.user_id === currentUser?.user_id;
  };

  // Render the existing comments section
  const renderExistingComments = () => {
    if (comments.length === 0) return null;
    
    return (
      <div className="space-y-3 max-h-60 overflow-y-auto pr-1 mb-3">
        {comments.map((comment) => (
          <div 
            key={comment.comment_id} 
            className={`p-2 rounded-lg border ${
              comment.isPending 
                ? 'border-amber-200 bg-amber-50' 
                : 'border-gray-100 bg-gray-50'
            }`}
          >
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center gap-1.5">
                <Typography variant="smallParagraph" className="font-medium">
                  {comment.user_name}
                </Typography>
                {comment.isPending && (
                  <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full flex items-center">
                    <Clock size={10} className="mr-0.5" />
                    Pending
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {canEditComment(comment) && (
                  <>
                    <button
                      onClick={() => handleEditComment(comment)}
                      className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Edit comment"
                      title="Edit comment"
                    >
                      <Edit2 size={12} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteComment(e, comment.comment_id)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Delete comment"
                      title="Delete comment"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <Typography variant="mediumParagraph" className="text-sm">
              {comment.comment}
            </Typography>
            <Typography variant="caption" color="gray" className="text-xs mt-1">
              {formatTimestamp(comment.created_at)}
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  if (showCommentInput) {
    return (
      <div 
        className="absolute z-10 mt-1 p-3 bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-md"
        style={{ [position]: '1rem', top: '100%' }}
        onClick={(e) => e.stopPropagation()}
        data-transcript-idx={transcriptIdx}
      >
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-1.5">
            <MessageSquare size={16} className="text-indigo-600" />
            <Typography variant="smallParagraph" className="font-medium">
              {isEditing ? 'Edit Comment' : (comments.length > 0 ? 'Add to Thread' : 'Add Comment')}
            </Typography>
          </div>
          <button 
            onClick={handleCloseCommentInput}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label="Close comment input"
          >
            <X size={14} />
          </button>
        </div>
        
        {/* Show existing comments when adding a new comment */}
        {!isEditing && comments.length > 0 && renderExistingComments()}
        
        <textarea
          ref={commentInputRef}
          value={commentText}
          onChange={handleCommentChange}
          onKeyDown={handleKeyDown}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          rows={3}
          placeholder="Type your comment here..."
        />
        
        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-2">
            <Typography variant="caption" color="gray" className="text-xs">
              Press Ctrl+Enter to save
            </Typography>
            
            {isEditing && (
              <button
                onClick={(e) => handleDeleteComment(e, editingCommentId!)}
                className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md flex items-center gap-1"
                aria-label="Delete comment"
              >
                <Trash2 size={12} />
                Delete
              </button>
            )}
          </div>
          
          <button
            data-save-comment-button
            onClick={handleSaveComment}
            disabled={commentText.trim() === ''}
            className={`px-3 py-1.5 rounded-md flex items-center gap-1 text-sm ${
              commentText.trim() === '' 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            <Send size={14} />
            {isEditing ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  if (showCommentThread) {
    return (
      <div 
        className="absolute z-10 mt-1 p-3 bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-md"
        style={{ [position]: '1rem', top: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-1.5">
            <MessageSquare size={16} className="text-indigo-600" />
            <Typography variant="smallParagraph" className="font-medium">
              Comments ({comments.length})
            </Typography>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={handleAddToThread}
              className="p-1.5 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
              aria-label="Add comment"
              title="Add comment"
            >
              <Plus size={16} />
            </button>
            <button 
              onClick={handleCloseCommentThread}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
              aria-label="Close comments"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        
        <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
          {comments.map((comment) => (
            <div 
              key={comment.comment_id} 
              className={`p-2 rounded-lg border ${
                comment.isPending 
                  ? 'border-amber-200 bg-amber-50' 
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center gap-1.5">
                  <Typography variant="smallParagraph" className="font-medium">
                    {comment.user_name}
                  </Typography>
                  {comment.isPending && (
                    <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full flex items-center">
                      <Clock size={10} className="mr-0.5" />
                      Pending
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {canEditComment(comment) && (
                    <>
                      <button
                        onClick={() => handleEditComment(comment)}
                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Edit comment"
                        title="Edit comment"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteComment(e, comment.comment_id)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Delete comment"
                        title="Delete comment"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <Typography variant="mediumParagraph" className="text-sm">
                {comment.comment}
              </Typography>
              <Typography variant="caption" color="gray" className="text-xs mt-1">
                {formatTimestamp(comment.created_at)}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}; 